import React, { useEffect, useState } from 'react';
import type { FormComponentProps } from 'antd/lib/form';
import { LoadingState } from 'Components/Layout';
import { ShowResult } from 'Components/Result';
import { useStoreState } from 'easy-peasy';
import { promisify } from 'es6-promisify';
import { useFormWrapper } from './utils';
import { api } from '~/api';
import { Button, Form } from 'Components/nui';
import LocationAlerts from './LocationAlerts';
import { submitError, submitSuccess } from 'Components/form/utils';
import { FormattedMessage } from 'react-intl';
import { useNewOrderSchema } from '~/hooks/useOrders';
import { useInvalidateMarketOrders } from '~/pages/Market/hooks/useMarketOrders';
import { tagMarketplaceTradingActivity } from '~/sentryMarketplace';

interface NFormProps extends FormComponentProps {
  order: any;
  product: any;
  schema: any;
  submitState: any;
  setCurCur: (currency: string) => void;
}

function NForm({
  form,
  order,
  product,
  schema,
  submitState,
  setCurCur,
}: NFormProps) {
  const solution = useStoreState((state) => state.auth.solution);
  const invalidateMarketOrders = useInvalidateMarketOrders();
  const [price, setPrice] = useState(order.price.val);
  const [displayedValue, setDisplayedValue] = useState<any>();

  const props = {
    form,
    order,
    product,
    schema,
    solution,
    initialData: {},
    price,
    setPrice,
    displayedValue,
  };

  const formWrapper = useFormWrapper(props);
  const validateFieldsAndScroll = promisify(form.validateFieldsAndScroll);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Client-side validation
    try {
      await validateFieldsAndScroll();
    } catch {
      return;
    }
    submitState.setLoading(true);
    const data = {
      ...formWrapper.serialize(),
      'fok-order-id': order.id,
    };
    try {
      const result = await api.getData(
        { type: 'orders', getResult: (x: any) => x },
        null,
        { method: 'post', data }
      );

      tagMarketplaceTradingActivity('counter_created', {
        order_id: order.id,
        counter_id: result?.counter?.id ?? result?.counter_id,
        order_type: order.type,
      });
      invalidateMarketOrders();
      submitState.setResult(result);
    } catch (err: any) {
      const errors = err?.response?.data?.errors?.[0]?.description;
      if (errors) {
        formWrapper.setErrors(errors);
      } else {
        submitState.setResult(err);
      }
    }
    submitState.setLoading(false);
  }

  const currency = formWrapper.form.getFieldValue('currency');

  useEffect(() => {
    if (!currency) return;
    setCurCur(currency);
  }, [currency, setCurCur]);

  const variant = form.getFieldValue('variant');

  return (
    <div>
      <Form onSubmit={handleSubmit}>
        <LocationAlerts
          form={form}
          order={order}
          price={price}
          setPrice={setPrice}
          setValue={setDisplayedValue}
          schema={schema}
          variant={variant}
        />
        {formWrapper.render()}
        <Form.Item>
          <Button
            className={order.type === 'bid' ? 'offer' : 'bid'}
            htmlType="submit"
            loading={submitState.loading}
          >
            {order.type === 'bid' ? (
              <FormattedMessage
                id="marketrow-fok-button-sell"
                description="button for fok 'Sell' in Marketrow"
                defaultMessage="Sell"
              />
            ) : (
              <FormattedMessage
                id="marketrow-fok-button-buy"
                description="button for fok 'Buy' in Marketrow"
                defaultMessage="Buy"
              />
            )}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

type IWrappedForm = (props: any) => React.ReactElement;
const WrappedForm: IWrappedForm = Form.create()(NForm) as any;

interface FokProps {
  order: any;
  product: any;
  setCurCur: (currency: string) => void;
}

export default ({ order, product, setCurCur }: FokProps) => {
  const { data: schema, isLoading, error } = useNewOrderSchema(order.id);

  const state = {
    loading: isLoading,
    error,
    value: schema,
  };

  return (
    <ShowResult
      renderSuccess={submitSuccess}
      renderError={submitError}
      handleModalClose={() => {}}
    >
      {(submitState: any) => (
        <div className="wrapper">
          <div className="nui-form order-form">
            <LoadingState state={product ? state : null}>
              {(schema: any) => (
                <WrappedForm
                  key={`fok-form-${order.id}`}
                  order={order}
                  schema={schema}
                  product={product}
                  submitState={submitState}
                  setCurCur={setCurCur}
                />
              )}
            </LoadingState>
          </div>
        </div>
      )}
    </ShowResult>
  );
};
